import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getStudentWhatsAppPreferences, saveStudentWhatsAppPreferences } from '../../api/whatsapp';
import Button from '../../components/common/Button';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import WhatsAppUnavailableNotice from '../../components/notifications/WhatsAppUnavailableNotice';
import { getApiErrorInfo } from '../../utils/apiError';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';

const TYPE_TOGGLE_KEYS = [
    { key: 'sessionReminders', labelKey: 'pages.notifications.sessionReminders' },
    { key: 'absenceAlerts', labelKey: 'pages.notifications.absenceAlerts' },
    { key: 'lowBalance', labelKey: 'pages.notifications.lowBalance' },
    { key: 'feeReminders', labelKey: 'pages.notifications.feeReminders' },
    { key: 'sessionCancelled', labelKey: 'pages.notifications.sessionCancelled' },
    { key: 'classroomStarted', labelKey: 'pages.notifications.classroomStarted' },
    { key: 'holidayAnnouncements', labelKey: 'pages.notifications.holidayAnnouncements' },
];

export default function StudentNotificationPreferences() {
    const { t } = useTranslation();
    const [prefs, setPrefs] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');

    const typeToggles = useMemo(
        () => TYPE_TOGGLE_KEYS.map((item) => ({ ...item, label: t(item.labelKey) })),
        [t],
    );

    useEffect(() => {
        setLoading(true);
        setErr('');
        getStudentWhatsAppPreferences()
            .then(setPrefs)
            .catch((e) => {
                setPrefs(null);
                setErr(getApiErrorInfo(e, t('pages.notifications.loadFailed')).message);
            })
            .finally(() => setLoading(false));
    }, [t]);

    const retryLoad = () => {
        setLoading(true);
        setErr('');
        getStudentWhatsAppPreferences()
            .then(setPrefs)
            .catch((e) => {
                setPrefs(null);
                setErr(getApiErrorInfo(e, t('pages.notifications.loadFailed')).message);
            })
            .finally(() => setLoading(false));
    };

    const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

    const persist = async () => {
        const saved = await saveStudentWhatsAppPreferences(prefs);
        setPrefs(saved);
        return saved;
    };
    const { markSaved } = useUnsavedChanges(prefs, {
        enabled: !!prefs,
        onSave: async () => { await persist(); },
    });

    const save = async () => {
        setSaving(true);
        setMsg('');
        setErr('');
        try {
            const saved = await persist();
            markSaved(saved);
            setMsg(t('pages.notifications.saved'));
        } catch {
            setErr(t('pages.notifications.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <PageLoadingShell
                variant="page-shell-form"
                title={t('pages.notifications.title')}
                subtitle={t('pages.notifications.subtitle')}
                showBack={false}
            />
        );
    }
    if (!prefs) {
        return (
            <div className="page-shell max-w-2xl mx-auto space-y-6">
                <PageHeaderSection
                    title={t('pages.notifications.title')}
                    subtitle={t('pages.notifications.subtitleShort')}
                />
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {err || t('pages.notifications.unavailable')}
                </div>
                <Button type="button" variant="secondary" onClick={retryLoad}>
                    {t('pages.notifications.tryAgain')}
                </Button>
            </div>
        );
    }

    const available = prefs.licenseWhatsAppEnabled && prefs.academyWhatsAppEnabled;

    return (
        <div className="page-shell max-w-2xl mx-auto space-y-6">
            <PageHeaderSection
                title={t('pages.notifications.title')}
                subtitle={t('pages.notifications.subtitle')}
            />

            {!available && <WhatsAppUnavailableNotice prefs={prefs} />}

            <div className={`bg-white rounded-xl border p-4 md:p-6 space-y-4 ${!available ? 'opacity-60' : ''}`}>
                <label className="flex items-start gap-3 py-2 border-b">
                    <input
                        type="checkbox"
                        className="h-5 w-5 mt-0.5"
                        checked={!!prefs.whatsappOptIn}
                        disabled={!available}
                        onChange={() => toggle('whatsappOptIn')}
                    />
                    <span>
                        <span className="block text-sm font-medium text-gray-900">{t('pages.notifications.optInTitle')}</span>
                        <span className="block text-xs text-gray-500 mt-1">{t('pages.notifications.optInHint')}</span>
                    </span>
                </label>

                {typeToggles.map(({ key, label }) => (
                    <label key={key} className="flex items-center justify-between gap-4 py-2 border-b last:border-0">
                        <span className="text-sm text-gray-800">{label}</span>
                        <input
                            type="checkbox"
                            className="h-5 w-5"
                            checked={!!prefs[key]}
                            disabled={!available || !prefs.whatsappOptIn}
                            onChange={() => toggle(key)}
                        />
                    </label>
                ))}

                {err && <p className="text-sm text-red-700">{err}</p>}

                <Button onClick={save} disabled={saving || !available}>
                    {saving ? t('pages.notifications.saving') : t('pages.notifications.savePreferences')}
                </Button>
                {msg && <p className="text-sm text-emerald-700">{msg}</p>}
            </div>
        </div>
    );
}
